import React, { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { DateTime } from "luxon";
import { getOrParseDateTime } from "@cpt/shared";
import { formatDateOnly } from "utils/formatters/DateFormatters";
import { OrderStatus } from "client/api-v2";
import type { OrderDto } from "client/api-v2";
import { ActionDialog } from "components/ActionDialog/ActionDialog";
import { AlertSeverity, useAlerts } from "components/AlertProvider/AlertProvider";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { useRbacContext } from "components/authorization/RbacProvider";
import { orderToasts } from "areas/orders/components/chrome/orderToasts";
import { archiveQuote, cancelQuote, publishQuote, reopenQuote, retractQuote } from "areas/orders/services/orderService";
import { describeOrderError } from "areas/orders/utils/orderErrors";

type Transition = "publish" | "revoke" | "cancel" | "reopen" | "archive";

interface TransitionDefinition {
  key: Transition;
  label: string;
  variant: "contained" | "outlined" | "text";
  color: "primary" | "error";
  sx?: SxProps<Theme>;
  size?: "small" | "medium";
  requiresSaved?: boolean;
  confirmTitle: string;
  confirmDescription: (order: OrderDto) => React.ReactNode;
  /** The confirming button's own words, which name the act rather than repeating the rail's label. */
  confirmText: string;
  /**
   * The confirming button's colour, by what the act does. Red says the act takes something away,
   * so publishing and reopening - which give the record back - are not red.
   */
  confirmColor: "primary" | "error";
  /** The dismissing button, where "Cancel" beside a cancellation would read as the act itself. */
  cancelText?: string;
  isAvailable: (order: OrderDto) => boolean;
  act: (odNumber: string) => Promise<OrderDto>;
  toast?: (order: OrderDto) => string;
}

/**
 * The window a published quote stays open for, mirroring `Order.ValidityDays`. Publishing starts it
 * from the day of publication, so a draft's expiry is a projection until then.
 */
const quoteValidityDays = 30;

function startOfTodayUtc(): DateTime {
  return DateTime.utc().startOf("day");
}

/**
 * The day this quote is set to close, as the publish confirmation states it: a chosen expiry where
 * one is set, the current window on an already-published quote, and otherwise the standard window
 * measured from today.
 * @param order
 */
function plannedExpiry(order: OrderDto): DateTime {
  const chosen = order.validUntilOverride ?? order.validUntil;
  return chosen ? getOrParseDateTime(chosen) : startOfTodayUtc().plus({ days: quoteValidityDays });
}

function publishDescription(order: OrderDto) {
  const expiry = plannedExpiry(order);
  const daysAway = Math.round(expiry.diff(startOfTodayUtc(), "days").days);

  return (
    <Stack spacing={1.5}>
      {order.isViaReseller ? (
        <>
          <Typography variant="body2">
            {`This quote should only be shared with the reseller ${order.resellerBillingAccountLegalName}.`}
          </Typography>
          <Typography variant="body2">
            {`${order.billingAccountLegalName} subscription owners will not see this quote in the customer portal.`}
          </Typography>
        </>
      ) : (
        <Typography variant="body2">
          {`${order.billingAccountLegalName}’s subscription owners will be able to see this quote in the customer portal.`}
        </Typography>
      )}
      <Typography variant="body2">
        {`This quote is currently set to expire on ${formatDateOnly(expiry)}, (${daysAway} days from now).`}
      </Typography>
    </Stack>
  );
}

export function canReopenOrder(order: OrderDto): boolean {
  return (
    (order.status === OrderStatus.Cancelled || order.status === OrderStatus.Expired) &&
    order.canReopen &&
    !order.isArchived
  );
}

// Every transition here can be refused by the server — most visibly Publish, which the application
// layer blocks on an unmeasured renewal, a second live quote on the subscription, or a billing
// account with no complete address. The refusal reaches the caller as an ordinary error message
// naming what is wrong, so it is rendered the same way any other failure here is: inline, with
// nothing further to click through to find it.
//
// Which transition each status offers is the availability matrix the domain guards
// (Order.EnsureTransitionFrom), not a presentation choice: an archived order offers none of them.
const transitions: TransitionDefinition[] = [
  {
    key: "publish",
    label: "Publish quote",
    variant: "contained",
    color: "primary",
    requiresSaved: true,
    confirmTitle: "Publish this quote?",
    confirmDescription: publishDescription,
    confirmText: "Publish quote",
    confirmColor: "primary",
    isAvailable: (order) => order.status === OrderStatus.New && !order.isArchived,
    act: publishQuote,
    // Named only where its subscription owners can really see the quote. A reseller order is never
    // shown to them.
    toast: (order) => orderToasts.published(order.isViaReseller ? undefined : order.billingAccountLegalName),
  },
  {
    key: "revoke",
    label: "Revoke access",
    variant: "text",
    color: "error",
    size: "small",
    requiresSaved: true,
    confirmTitle: "Revoke customer access?",
    confirmDescription: (order) =>
      order.isViaReseller
        ? "The quote will no longer be available."
        : `The quote will no longer be available to ${order.billingAccountLegalName} in the customer portal.`,
    confirmText: "Revoke access",
    confirmColor: "error",
    isAvailable: (order) => order.status === OrderStatus.Quote && !order.isArchived,
    act: retractQuote,
    toast: () => orderToasts.accessRevoked,
  },
  {
    key: "cancel",
    label: "Cancel order",
    variant: "text",
    color: "error",
    size: "small",
    requiresSaved: true,
    confirmTitle: "Cancel this order?",
    confirmDescription: (order) =>
      `This cancels the order for ${order.billingAccountLegalName}. It can no longer be viewed or shared.`,
    confirmText: "Cancel order",
    confirmColor: "error",
    cancelText: "Keep order",
    isAvailable: (order) =>
      (order.status === OrderStatus.New || order.status === OrderStatus.Quote) && !order.isArchived,
    act: cancelQuote,
  },
  {
    key: "reopen",
    label: "Reopen order",
    variant: "outlined",
    color: "primary",
    confirmTitle: "Reopen this order?",
    confirmDescription: (order) =>
      `This sets the order for ${order.billingAccountLegalName} back to New status so it can be edited and re-quoted.`,
    confirmText: "Reopen order",
    confirmColor: "primary",
    isAvailable: canReopenOrder,
    act: reopenQuote,
    toast: () => orderToasts.reopened,
  },
  {
    key: "archive",
    label: "Archive",
    variant: "text",
    color: "primary",
    sx: (theme) => ({ color: theme.palette.text.primary }),
    size: "small",
    requiresSaved: true,
    confirmTitle: "Archive order",
    confirmDescription: () => "This puts the order away for good. It is one-way, and its status is unchanged.",
    confirmText: "Archive",
    confirmColor: "error",
    cancelText: "Keep order",
    isAvailable: (order) =>
      (order.status === OrderStatus.New || order.status === OrderStatus.Quote) && !order.isArchived,
    act: archiveQuote,
    toast: () => orderToasts.archived,
  },
];

/**
 * Publishing and revoking are the same decision at two points in the quote's life.
 * @param transition
 */
function isPrimary(transition: TransitionDefinition): boolean {
  return transition.key === "publish" || transition.key === "revoke";
}

export function disabledLookSx(theme: Theme, variant: TransitionDefinition["variant"]) {
  return {
    color: theme.palette.action.disabled,
    ...(variant === "contained" && {
      backgroundColor: theme.palette.action.disabledBackground,
      boxShadow: "none",
      "&:hover": { backgroundColor: theme.palette.action.disabledBackground, boxShadow: "none" },
    }),
  };
}

export function OrderActionPreventedByUnsavedChangesDialog({
  open,
  onDismiss,
}: {
  open: boolean;
  onDismiss: () => void;
}) {
  return (
    <Dialog open={open} onClose={onDismiss}>
      <DialogTitle>Save your changes first</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This quote has unsaved changes. Save or discard them before performing this action.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onDismiss} autoFocus>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function useTransitionPrompt(order: OrderDto, onChanged: (order: OrderDto) => void) {
  const { sendNewAlert } = useAlerts();
  const [pending, setPending] = useState<TransitionDefinition | undefined>(undefined);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  const prompt = (transition: TransitionDefinition) => {
    setError(undefined);
    setPending(transition);
  };

  const confirm = async () => {
    if (!pending) {
      return;
    }
    setActing(true);
    setError(undefined);
    try {
      onChanged(await pending.act(order.odNumber));
      if (pending.toast) {
        sendNewAlert(pending.toast(order), AlertSeverity.Success);
      }
      setPending(undefined);
    } catch (actError) {
      // The dialog stays open so the representative sees which action failed and why, rather than
      // the error appearing after the prompt has already closed.
      setError(actError);
    } finally {
      setActing(false);
    }
  };

  const dialog = pending && (
    <ActionDialog
      title={pending.confirmTitle}
      description={
        <>
          {pending.confirmDescription(order)}
          {!!error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {describeOrderError(error)}
            </Alert>
          )}
        </>
      }
      show={!!pending}
      confirmText={pending.confirmText}
      confirmColor={pending.confirmColor}
      cancelText={pending.cancelText}
      onConfirm={confirm}
      onCancel={() => {
        if (!acting) {
          setPending(undefined);
        }
      }}
    />
  );

  return { prompt, dialog };
}

export function PrimaryLifecycleAction({
  order,
  onChanged,
  isDirty,
}: {
  order: OrderDto;
  onChanged: (order: OrderDto) => void;
  isDirty: boolean;
}) {
  const primary = transitions.filter((transition) => transition.isAvailable(order)).find(isPrimary);
  const { prompt, dialog } = useTransitionPrompt(order, onChanged);
  const [blocked, setBlocked] = useState(false);

  if (!primary) {
    return null;
  }

  const blockedBySave = isDirty && !!primary.requiresSaved;

  return (
    <>
      <Button
        fullWidth={primary.key === "publish"}
        variant={primary.variant}
        color={primary.color}
        size={primary.size}
        disableElevation
        sx={blockedBySave ? (theme) => disabledLookSx(theme, primary.variant) : primary.sx}
        onClick={() => (blockedBySave ? setBlocked(true) : prompt(primary))}
      >
        {primary.label}
      </Button>
      {dialog}
      <OrderActionPreventedByUnsavedChangesDialog open={blocked} onDismiss={() => setBlocked(false)} />
    </>
  );
}

export function SecondaryLifecycleActions({
  order,
  onChanged,
  isDirty,
}: {
  order: OrderDto;
  onChanged: (order: OrderDto) => void;
  isDirty: boolean;
}) {
  const secondary = transitions.filter(
    (transition) => transition.isAvailable(order) && !isPrimary(transition) && transition.key !== "reopen"
  );
  const { prompt, dialog } = useTransitionPrompt(order, onChanged);
  const [blocked, setBlocked] = useState(false);

  if (secondary.length === 0) {
    return null;
  }

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ justifyContent: "space-between", px: 2 }}>
        {secondary.map((transition) => {
          const blockedBySave = isDirty && !!transition.requiresSaved;
          return (
            <Button
              key={transition.key}
              variant={transition.variant}
              color={transition.color}
              size={transition.size}
              sx={blockedBySave ? (theme) => disabledLookSx(theme, transition.variant) : transition.sx}
              onClick={() => (blockedBySave ? setBlocked(true) : prompt(transition))}
            >
              {transition.label}
            </Button>
          );
        })}
      </Stack>
      {dialog}
      <OrderActionPreventedByUnsavedChangesDialog open={blocked} onDismiss={() => setBlocked(false)} />
    </>
  );
}

export function ReopenOrderAction({ order, onChanged }: { order: OrderDto; onChanged: (order: OrderDto) => void }) {
  const { isAuthorized } = useRbacContext();
  const reopen = transitions.find((transition) => transition.key === "reopen" && transition.isAvailable(order));
  const { prompt, dialog } = useTransitionPrompt(order, onChanged);

  if (!reopen || !isAuthorized(RbacPolicies.Orders.ReadWrite)) {
    return null;
  }

  return (
    <>
      <Button
        variant={reopen.variant}
        color={reopen.color}
        sx={{ whiteSpace: "nowrap" }}
        onClick={() => prompt(reopen)}
      >
        {reopen.label}
      </Button>
      {dialog}
    </>
  );
}
